import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import PageHero from "@/components/PageHero";
import ImageFrame from "@/components/ImageFrame";
import CTABand from "@/components/CTABand";
import NeuralSignalCanvas from "@/components/NeuralFrequencyCanvas";
import { Reveal, MaskedLines, ChapterLabel, EASE } from "@/components/motion";
import { IMAGES } from "@/data/content";

const PILLARS = [
  {
    title: "Måling",
    text: "Små sensorer på hovedbunden registrerer hjernens elektriske aktivitet, helt ned i de langsomste, infra-lave frekvenser. Sensorerne måler kun. De tilfører ikke elektrisk stimulation.",
  },
  {
    title: "Feedback",
    text: "Aktiviteten omsættes til feedback gennem lyd eller billede. Feedbacken ændrer sig løbende i takt med den aktivitet, der registreres.",
  },
  {
    title: "Tilpasning",
    text: "Feedbacken indgår i træningen af hjernens selvregulering. Gennem gentagelse arbejdes der med hjernens fleksibilitet og evne til at tilpasse sig.",
  },
];

const SESSION_STEPS = [
  {
    title: "Sensorer på hovedbunden",
    text: "Små sensorer placeres på huden. Det mærkes næsten ikke og gør ikke ondt. Sensorerne registrerer hjernens aktivitet. De tilfører ikke elektrisk stimulation.",
  },
  {
    title: "Du ser, lytter eller spiller",
    text: "Under træningen følger du en rolig skærm, lyd eller en enkel visualisering. Du behøver hverken koncentrere dig eller gøre noget særligt.",
  },
  {
    title: "Feedback i realtid",
    text: "Feedbacken ændrer sig løbende i takt med den aktivitet, sensorerne registrerer, og indgår i træningen af hjernens selvregulering.",
  },
  {
    title: "Ca. 40 minutter i ro",
    text: "En session varer ca. 40 minutter og indeholder typisk omkring 30 minutters neurofeedbacktræning. Undervejs følges responsen, og træningen tilpasses individuelt.",
  },
];

const FAQ = [
  {
    q: "Hvad er ILF Neurofeedback?",
    a: "ILF står for Infra-Low Frequency. Metoden er udviklet af Sue og Siegfried Othmer og arbejder med meget langsomme udsving i hjernens elektriske aktivitet. Under træningen får hjernen løbende feedback om sin egen aktivitet, og træningen tilpasses individuelt ud fra den enkeltes respons.",
  },
  {
    q: "Gør neurofeedback ondt?",
    a: "Nej. Sensorerne sidder på hovedbunden og registrerer kun hjernens egen aktivitet. De tilfører ikke elektrisk stimulation. De fleste oplever træningen som rolig og behagelig.",
  },
  {
    q: "Hvor mange træninger skal man bruge?",
    a: "Center for Hjerneregulering arbejder som udgangspunkt med forløb på minimum 20 træninger. Nogle oplever ændringer relativt tidligt i forløbet, mens andre har brug for længere tid. Der evalueres systematisk med spørgeskemaer efter 10 og 20 træninger, og herefter vurderes det individuelt, om yderligere træning giver mening.",
  },
  {
    q: "Kan man træne neurofeedback, hvis man tager medicin eller går i terapi?",
    a: "Ja. Neurofeedback kan trænes både alene og sideløbende med medicin eller terapi. Træningen tilpasses individuelt og kræver ikke, at man stopper andre igangværende indsatser.",
  },
  {
    q: "Er der bivirkninger?",
    a: "De fleste oplever ingen bivirkninger. Enkelte mærker en forbigående træthed efter de første træninger, ligesom ved anden form for træning. Træningen tilpasses løbende, så den forbliver behagelig.",
  },
  {
    q: "Kan børn få neurofeedback?",
    a: "Ja. Træningen tilpasses barnets alder og tålmodighed, og for mange børn føles det som at følge et roligt spil.",
  },
  {
    q: "Er neurofeedback videnskabeligt dokumenteret?",
    a: "Neurofeedback er undersøgt inden for en række områder, blandt andet ADHD og opmærksomhed. Forskningen viser lovende resultater, men evidensen varierer mellem metoder, studiedesign og problemstillinger, og effekten er forskellig fra person til person. Derfor lover Center for Hjerneregulering aldrig et bestemt resultat.",
  },
];

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-hairline">
      <button
        type="button"
        data-testid={`faq-trigger-${index}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-serif text-xl tracking-tight text-charcoal sm:text-2xl">{q}</span>
        <Plus
          className={`h-5 w-5 shrink-0 text-graphite transition-transform duration-300 ${open ? "rotate-45" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-3xl pb-7 text-base leading-relaxed text-graphite">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Neurofeedback = () => (
  <main data-testid="page-neurofeedback">
    <PageHero
      testId="hero-neurofeedback"
      overline="Metoden"
      title={["Neurofeedback:", "Et levende spejl", "til hjernen."]}
      lead="Center for Hjerneregulering arbejder med ILF Neurofeedback (Infra-Low Frequency) efter Othmer-metoden. Træningen tilpasses individuelt og tager udgangspunkt i den enkeltes respons med fokus på hjernens regulering."
    />

    <section className="border-b border-hairline">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <ChapterLabel number="01" label="Hvad er neurofeedback?" />
        </Reveal>
        <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
          <MaskedLines mode="view" lines={["Tre enkle principper"]} />
        </h2>
        <Reveal delay={0.1} className="mt-8 max-w-3xl">
          <p className="text-base leading-relaxed text-graphite">
            Neurofeedback er hjernetræning baseret på feedback fra hjernens egen aktivitet. Sensorer på
            hovedbunden registrerer hjernens elektriske aktivitet, som omsættes til feedback gennem lyd
            eller billede. Feedbacken ændrer sig løbende i takt med den aktivitet, der registreres, og
            indgår i træningen af hjernens selvregulering.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-px border border-sand bg-sand md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="bg-canvas">
              <div className="h-full p-8 transition-colors duration-300 hover:bg-surface lg:p-10">
                <p className="font-mono text-[11px] tracking-[0.2em] text-muted">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-serif text-2xl tracking-tight text-charcoal sm:text-3xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-graphite">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-obsidian text-[#F5F5F0]">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <ChapterLabel number="02" label="Infra-Low Frequency" dark />
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2 className="font-serif text-3xl leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              <MaskedLines
                mode="view"
                lines={[
                  "Hjernens langsomste rytmer",
                  <span key="v" className="font-light italic text-[#A3A299]">
                    er metodens fokus.
                  </span>,
                ]}
              />
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-xl space-y-5 text-base leading-relaxed text-[#A3A299]">
              <p>
                ILF Neurofeedback er udviklet af ægteparret Sue og Siegfried Othmer, der har arbejdet med
                metoden i over tre årtier. ILF står for Infra-Low Frequency, fordi træningen arbejder med
                meget langsomme komponenter af EEG-signalet, ned under 0,1 Hz. I Othmer-modellens teoretiske
                forståelse spiller disse langsomme udsving en rolle for nervesystemets regulering og arousal,
                og metoden anvendes netop med fokus på regulering.
              </p>
              <p>
                Det er værd at vide, at denne forståelse er Othmer-modellens egen teoretiske ramme.
                Forskningen i de infra-lave EEG-frekvensers betydning er fortsat under udvikling og har ikke
                samme etablerede konsensus som for eksempel viden om neuroplasticitet.
              </p>
              <p>
                I stedet for at arbejde med faste protokoller tilpasses træningen løbende ud fra, hvordan
                den enkelte reagerer. Derfor er ikke to forløb ens.
              </p>
            </Reveal>
          </div>
          <div className="flex flex-col justify-end lg:col-span-6">
            <Reveal delay={0.2}>
              <NeuralSignalCanvas />
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-hairline bg-surface">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-7">
          <Reveal>
            <ChapterLabel number="03" label="Sådan foregår en træning" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Ro, feedback,", "gentagelse."]} />
          </h2>
          <div className="mt-12">
            {SESSION_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div className="grid grid-cols-[3rem_1fr] gap-4 border-t border-sand py-7 sm:grid-cols-[4rem_1fr]">
                  <span className="font-mono text-xs tracking-[0.2em] text-muted">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl tracking-tight text-charcoal">{s.title}</h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-graphite">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <ImageFrame
              src={IMAGES.session}
              alt="Klient i stol under neurofeedbacktræning med skærmen foran"
              caption="Fig. 01 — Træningen foregår i ro"
              testId="neurofeedback-session-img"
              tone="none"
              parallax={false}
            />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-b border-hairline">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-6">
          <Reveal>
            <ChapterLabel number="04" label="Neuroplasticitet" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Træning og", "neuroplasticitet"]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p>
              Hjernen er dynamisk. Den formes af erfaring gennem hele livet, reagerer på det, den oplever,
              og kan forandre sig over tid. Det er baggrunden for, at træning overhovedet giver mening.
            </p>
            <p>
              Målet er ikke en bestemt tilstand, der kun findes på centret. Målet er at arbejde med hjernens
              fleksibilitet, så reguleringen bliver lettere i hverdagen, i skolen, på arbejdet og i søvnen.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal>
            <ChapterLabel number="05" label="Viden og faglighed" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Viden, erfaring og", "en metode i udvikling"]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p>
              Neurofeedback bygger på hjernens evne til at lære og tilpasse sig gennem feedback. Metoden
              er undersøgt inden for en række områder, blandt andet opmærksomhed, ADHD og selvregulering,
              og forskningen udvikler sig løbende.
            </p>
            <p>
              I Center for Hjerneregulering arbejder jeg med ILF Neurofeedback og med udgangspunkt i det
              enkelte menneske. Træningen tilpasses løbende efter respons, fordi to hjerner ikke
              nødvendigvis reagerer ens.
            </p>
            <p>
              Mit udgangspunkt er enkelt: faglighed, nysgerrighed og respekt for det, vi ved – og det, vi
              fortsat lærer om hjernen.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section>
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <ChapterLabel number="06" label="Ofte stillede spørgsmål" />
        </Reveal>
        <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
          <MaskedLines mode="view" lines={["Spørgsmål & svar"]} />
        </h2>
        <Reveal delay={0.15} className="mt-10">
          <div className="border-b border-hairline">
            {FAQ.map((f, i) => (
              <FAQItem key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <CTABand
      title="Nysgerrig på, hvad din hjerne kan?"
      sub="Book en indledende samtale. Jeg gennemgår metoden, dine mål og dine spørgsmål, helt uden forpligtelser."
      showMethodLink={false}
    />
  </main>
);

export default Neurofeedback;
